import React, { FC, useEffect, useState } from "react";
import { Formik, FormikValues, Form, FormikHelpers } from "formik";
import { Box, Button, MenuItem, Stack, Typography } from "@mui/material";
import toast from "react-hot-toast";
import { FieldText } from "../../../../../../components/fields/Text/Text";
import { AntSwitch } from "../../../../videos/selectedVideoCardV2/AntSwitchDesign";
import { IEmbed } from "../../../embedsV2/interface";
import { parseSettings } from "./helper";
import { FieldSelect } from "../../../../../../components/fields/Select";
import { updateEmbedSettings } from "../../../../../../api/endpoints";
import axiosInstant from "../../../../../../api";
import { ICreateEmbed } from "../constants";

interface Props {
  embed?: IEmbed;
  height?: number;
  onComplete: (data: any) => void;
  updateEmbed: (data: Partial<ICreateEmbed>) => void;
  onCreateStory?: () => void;
}

export const ShopabilitySettings: FC<Props> = ({
  embed,
  height = 200,
  onComplete,
  updateEmbed,
  onCreateStory,
}) => {
  const [initialValues, setInitialValues] = useState<any>();

  const handleSubmit = async (
    values: FormikValues,
    { setSubmitting }: FormikHelpers<typeof initialValues>
  ) => {
    if (embed?.id) {
      try {
        let response = {} as any;

        const prevSettings = parseSettings(embed);
        response = await axiosInstant.patch(updateEmbedSettings(embed.id), {
          settings: JSON.stringify({ ...prevSettings, annotations: [values] }),
        });

        const { status, data } = response;
        onComplete?.(data);
        if (status === 200 || status === 201)
          toast.success(`Story has been updated.`);
      } catch (error) {
        toast.error((error as Error).message);
      }
    } else onCreateStory?.();

    setSubmitting(false);
  };

  const handlePublishedChange = async () => {
    const updatedValues = {
      ...initialValues,
      enabled: !initialValues?.enabled,
    };
    setInitialValues(updatedValues);
    // updateEmbed({ shopability: updatedValues });
  };

  const getInput = (label: string, name: string) => (
    <FieldText
      name={name}
      size="small"
      label={label}
      placeholder={label}
      className="border-0 rounded-xl bg-surface-2 !w-[335px]"
    />
  );

  useEffect(() => {
    if (embed) {
      const settings = parseSettings(embed);
      if (settings?.annotations?.length)
        setInitialValues({
          ...settings.annotations[0],
        });
    }
  }, [embed]);

  return (
    <Formik
      initialValues={initialValues || {}}
      enableReinitialize
      onSubmit={handleSubmit}
    >
      {(props) => (
        <Form>
          {!embed && props.values
            ? updateEmbed({ shopability: props.values })
            : null}
          <div
            className="flex flex-col gap-2 justify-between p-6 pb-0 relative"
            style={{ height }}
          >
            <Stack width={1} spacing={3}>
              <div className="border-b border-border-token pb-6 flex items-center gap-4">
                <AntSwitch
                  checked={initialValues?.enabled}
                  inputProps={{ "aria-label": "ant design" }}
                  onClick={handlePublishedChange}
                />
                <Typography>Enabled</Typography>
              </div>

              {initialValues?.enabled && (
                <Box className="flex flex-col gap-6 w-full">
                  {getInput("Call to action text", "text")}
                  <Box className="flex gap-4">
                    {getInput("Start Time", "startTime")}
                    {getInput("Target URL", "targetUrl")}
                  </Box>
                  <Box className="flex gap-4">
                    {getInput("Target class", "targetClass")}
                    <FieldSelect
                      size="small"
                      name="position"
                      label="Position"
                      required={false}
                      className="border-0 rounded-xl bg-surface-2 !w-[335px]"
                      placehodler="Position"
                    >
                      <MenuItem value="center">Center</MenuItem>
                      <MenuItem value="topLeft">Top Left</MenuItem>
                      <MenuItem value="topRight">Top Right</MenuItem>
                      <MenuItem value="botLeft">Bottom Left</MenuItem>
                      <MenuItem value="botRight">Bottom Right</MenuItem>
                    </FieldSelect>
                  </Box>
                </Box>
              )}
            </Stack>
            <div className="border-t border-border-token pt-4 flex items-center justify-end">
              <Button
                size="large"
                type="submit"
                variant="contained"
                disableElevation
                sx={{ textTransform: "capitalize" }}
              >
                Save Changes
              </Button>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
};
